import DOMPurify from 'dompurify';
import styled from 'styled-components';
import { linkButtonStyles } from '../Button/Button';
import { focusVisibleStyles, standardTransitionStyles } from '../../sharedStyles';

const StyledRenderHtmlString = styled.span`
  white-space: pre-line;

  a {
    ${linkButtonStyles}
    ${standardTransitionStyles}
    ${focusVisibleStyles}
  }
`;

export const parseLinksInString = (str: string, disableBold: boolean = false): string => {
  const linkRegex =
    /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi;

  const replacer = (matched: string) => {
    // Check if the URL has trailing punctuation
    const trailingPunctuation = matched.match(/[.,;:!?]+$/);
    const cleanUrl = matched.replace(/[.,;:!?]+$/, '');
    let withProtocol = cleanUrl;

    if (!withProtocol.startsWith('http')) {
      withProtocol = 'http://' + cleanUrl;
    }

    return `<a href="${withProtocol}" target="_blank" rel="noopener noreferrer">${cleanUrl} <span class="sr-only">Opens in a new tab</span></a>${trailingPunctuation ? trailingPunctuation[0] : ''}`;
  };

  // Just in case there's anything malicious in the string because
  // this is inserted into an html element using dangerouslySetInnerHTML
  const sanitizedStr = DOMPurify.sanitize(str);

  let processedStr = sanitizedStr;

  if (disableBold) {
    processedStr = processedStr.replace(/<\/?(b|strong)>/gi, '');
  }

  const linkifiedStr = processedStr.replace(linkRegex, replacer);
  const brokenUpStr = linkifiedStr.replace(/\n/g, '<br/>');

  return DOMPurify.sanitize(brokenUpStr, { ADD_ATTR: ['target'] });
};

export type RenderHtmlStringProps = {
  children: string;
  className?: string;
  disableBold?: boolean;
};

/**
 * Parses a string and renders it in an HTML element.
 *
 * The string will be sanitized and urls will be converted into <a> elements that open in a new tab.
 */
export const RenderHtmlString = ({ children, className = '', disableBold = false }: RenderHtmlStringProps) => {
  return (
    <StyledRenderHtmlString
      dangerouslySetInnerHTML={{ __html: parseLinksInString(children, disableBold) }}
      className={className}
    />
  );
};
